import { useState, useEffect, useRef, useCallback } from 'react';
import { useToast } from '@chakra-ui/react';
import { getTranslations } from '../translations';
import { debugLog, debugError } from '../utils/debug';
import { useAudio } from './useAudio';

export const useWorkoutTimer = (workTime, restTime, rounds, startupTime, soundEnabled = true, restTimeRules = []) => {
    const t = getTranslations();
    const toast = useToast();
    const { playBeep, cleanupAudioContext } = useAudio();

    const [isActive, setIsActive] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [timeLeft, setTimeLeft] = useState(0);
    const [totalTimeLeft, setTotalTimeLeft] = useState(0);
    const [currentPhase, setCurrentPhase] = useState('ready'); // 'ready', 'countdown', 'work', 'rest', 'finished'
    const [currentRound, setCurrentRound] = useState(1);

    const timerRef = useRef(null);

    // Helper function to get rest time for a specific round
    const getRestTimeForRound = useCallback((round) => {
        if (!restTimeRules || restTimeRules.length === 0) {
            return restTime;
        }

        // Valuta le regole in ordine inverso (le più specifiche per ultime)
        const reversedRules = [...restTimeRules].reverse();
        for (let rule of reversedRules) {
            if (rule.type === 'after' && round >= rule.round) {
                return rule.restTime;
            }
            if (rule.type === 'every' && round % rule.interval === 0) {
                return rule.restTime;
            }
            if (rule.type === 'specific' && rule.rounds && rule.rounds.includes(round)) {
                return rule.restTime;
            }
        }
        return restTime;
    }, [restTime, restTimeRules]);

    const startTimer = useCallback(() => {
        debugLog('useWorkoutTimer', 'startTimer', 'Starting timer...');

        // Cleanup existing timer
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }

        // Reset completo dello stato prima di avviare
        setIsActive(false);
        setIsPaused(false);
        setCurrentPhase('ready');
        setTimeLeft(0);
        setCurrentRound(1);
        setTotalTimeLeft(0);

        // Use RAF to ensure state update before restart
        requestAnimationFrame(() => {
            // Calcola il tempo totale considerando i tempi di riposo variabili
            let totalRestTime = 0;
            for (let r = 1; r < rounds; r++) {
                totalRestTime += getRestTimeForRound(r);
            }
            const totalCalculatedTime = startupTime + (workTime * rounds) + totalRestTime;
            setIsActive(true);
            setIsPaused(false);
            setCurrentPhase('countdown');
            setTimeLeft(startupTime);
            setTotalTimeLeft(totalCalculatedTime);
        });

    }, [startupTime, workTime, rounds, getRestTimeForRound]);

    const togglePause = useCallback(() => {
        if (isPaused) {
            setIsPaused(false);
            setIsActive(true);
        } else {
            setIsPaused(true);
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        }
    }, [isPaused]);

    const resetTimer = useCallback(() => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        setIsActive(false);
        setIsPaused(false);
        setCurrentPhase('ready');
        setTimeLeft(0);
        setTotalTimeLeft(0);
        setCurrentRound(1);
        cleanupAudioContext();
    }, [cleanupAudioContext]);

    const calculateTotalTimeLeft = useCallback((phase, round, timeLeft) => {
        if (phase === 'countdown') {
            // Calcola il tempo totale considerando i tempi di riposo variabili
            let totalRestTime = 0;
            for (let r = 1; r < rounds; r++) {
                totalRestTime += getRestTimeForRound(r);
            }
            return timeLeft + (workTime * rounds) + totalRestTime;
        } else if (phase === 'work') {
            const remainingRounds = rounds - round;
            let totalRestTime = 0;
            for (let r = round; r < rounds; r++) {
                totalRestTime += getRestTimeForRound(r);
            }
            return timeLeft + (workTime * remainingRounds) + totalRestTime;
        } else if (phase === 'rest') {
            const remainingRounds = rounds - round;
            let totalRestTime = 0;
            for (let r = round; r < rounds; r++) {
                totalRestTime += getRestTimeForRound(r);
            }
            return timeLeft + (workTime * remainingRounds) + totalRestTime;
        }
        return 0;
    }, [workTime, rounds, getRestTimeForRound]);

    // Timer Interval Effect
    useEffect(() => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }

        if (isActive && !isPaused && timeLeft > 0) {
            timerRef.current = setInterval(() => {
                setTimeLeft(prev => {
                    const newTimeLeft = prev > 0 ? prev - 1 : 0;

                    // Play beep logic - suoni diversi per work e rest (solo se suono è abilitato)
                    if (soundEnabled && newTimeLeft <= 5 && newTimeLeft > 0 && (currentPhase === 'work' || currentPhase === 'rest' || currentPhase === 'countdown')) {
                        playBeep(newTimeLeft, currentPhase);
                    }
                    return newTimeLeft;
                });

                setTotalTimeLeft(prev => prev > 0 ? prev - 1 : 0);
            }, 1000);
        }

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [isActive, isPaused, timeLeft, currentPhase, playBeep]);

    // Phase Logic Effect
    useEffect(() => {
        if (!isActive) return;

        if (timeLeft === 0) {
            if (currentPhase === 'countdown') {
                setCurrentPhase('work');
                setTimeLeft(workTime);
                // Non ricalcoliamo totalTimeLeft - il timer continua a decrementarlo normalmente
            } else if (currentPhase === 'work') {
                if (currentRound < rounds) {
                    setCurrentPhase('rest');
                    const restTimeForRound = getRestTimeForRound(currentRound);
                    setTimeLeft(restTimeForRound);
                    // Non ricalcoliamo totalTimeLeft - il timer continua a decrementarlo normalmente
                } else {
                    // Finished
                    setIsActive(false);
                    setCurrentPhase('finished');
                    setTotalTimeLeft(0);
                    toast({ title: t.workoutComplete, status: 'success', duration: 3000, isClosable: true });
                    cleanupAudioContext();
                }
            } else if (currentPhase === 'rest') {
                const nextRound = currentRound + 1;
                setCurrentRound(nextRound);
                setCurrentPhase('work');
                setTimeLeft(workTime);
                // Non ricalcoliamo totalTimeLeft - il timer continua a decrementarlo normalmente
            }
        }
    }, [timeLeft, isActive, currentPhase, currentRound, rounds, workTime, getRestTimeForRound, t, toast, cleanupAudioContext]);

    const goToNextStep = useCallback(() => {
        if (!isActive || isPaused) return;
        
        // Pausa il timer durante la navigazione
        setIsPaused(true);
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        
        let newPhase = currentPhase;
        let newTimeLeft = timeLeft;
        let newRound = currentRound;
        
        if (currentPhase === 'countdown') {
            newPhase = 'work';
            newTimeLeft = workTime;
        } else if (currentPhase === 'work') {
            if (currentRound < rounds) {
                newPhase = 'rest';
                newTimeLeft = getRestTimeForRound(currentRound);
            } else {
                // Finished
                setIsActive(false);
                setCurrentPhase('finished');
                toast({ title: t.workoutCompleted, status: 'success', duration: 3000, isClosable: true });
                cleanupAudioContext();
                return;
            }
        } else if (currentPhase === 'rest') {
            newRound = currentRound + 1;
            newPhase = 'work';
            newTimeLeft = workTime;
        }
        
        setCurrentPhase(newPhase);
        setTimeLeft(newTimeLeft);
        setCurrentRound(newRound);
        setTotalTimeLeft(calculateTotalTimeLeft(newPhase, newRound, newTimeLeft));
        
        // Riprendi il timer
        setIsPaused(false);
    }, [isActive, isPaused, currentPhase, currentRound, timeLeft, rounds, workTime, restTime, t, toast, cleanupAudioContext, calculateTotalTimeLeft, getRestTimeForRound]);

    const goToPreviousStep = useCallback(() => {
        if (!isActive || isPaused) return;
        if (currentPhase === 'countdown') return; // Non si può andare indietro dal countdown
        
        // Pausa il timer durante la navigazione
        setIsPaused(true);
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        
        let newPhase = currentPhase;
        let newTimeLeft = timeLeft;
        let newRound = currentRound;
        
        if (currentPhase === 'work') {
            if (currentRound === 1) {
                newPhase = 'countdown';
                newTimeLeft = startupTime;
            } else {
                newRound = currentRound - 1;
                newPhase = 'rest';
                newTimeLeft = getRestTimeForRound(newRound);
            }
        } else if (currentPhase === 'rest') {
            newPhase = 'work';
            newTimeLeft = workTime;
        }
        
        setCurrentPhase(newPhase);
        setTimeLeft(newTimeLeft);
        setCurrentRound(newRound);
        setTotalTimeLeft(calculateTotalTimeLeft(newPhase, newRound, newTimeLeft));
        
        // Riprendi il timer
        setIsPaused(false);
    }, [isActive, isPaused, currentPhase, currentRound, timeLeft, rounds, workTime, restTime, startupTime, calculateTotalTimeLeft, getRestTimeForRound]);

    return {
        isActive,
        isPaused,
        timeLeft,
        totalTimeLeft,
        currentPhase,
        currentRound,
        startTimer,
        togglePause,
        resetTimer,
        goToNextStep,
        goToPreviousStep
    };
};
